/**
 * Daily puzzle page.
 * Main puzzle view.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { DailyResponse } from '@daily-puzzle/shared';
import { Status } from '@daily-puzzle/shared';
import { fetchDaily } from '../api/client';
import { Board } from '../components/Board';
import { InstrumentHeader } from '../components/InstrumentHeader';
import { ActionRow } from '../components/ActionRow';
import { ResultModal } from '../components/ResultModal';
import { useGameState } from '../hooks/useGameState';
import './Daily.css';

export function Daily() {
    const { date } = useParams<{ date?: string }>();
    const navigate = useNavigate();

    const [dailyData, setDailyData] = useState<DailyResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [shareCopied, setShareCopied] = useState(false);
    const [progress, setProgress] = useState({ status: Status.InProgress, attemptsUsed: 0, stepIndex: 0 });

    // Load daily data
    useEffect(() => {
        async function load() {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchDaily(date);
                setDailyData(data);
                setProgress({
                    status: data.progress.status as Status,
                    attemptsUsed: data.progress.attempts_used,
                    stepIndex: data.progress.step_index,
                });

                // Show modal if already finished
                if (data.progress.status !== 'in_progress') {
                    setShowModal(true);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load puzzle');
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [date]);

    // Handle status changes from game state
    const handleStatusChange = useCallback((status: Status, attemptsUsed: number, stepIndex: number) => {
        setProgress({ status, attemptsUsed, stepIndex });
        if (status !== Status.InProgress) {
            setShowModal(true);
        }
    }, []);

    const { gameState, fen, lastMove, handleMove, handleReveal, toast } = useGameState(
        dailyData,
        handleStatusChange
    );

    // Share handler
    const handleShare = useCallback(async () => {
        if (!dailyData) return;

        try {
            await navigator.clipboard.writeText(dailyData.share.text);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        } catch {
            // Fallback for older browsers
            console.error('Failed to copy');
        }
    }, [dailyData]);

    if (loading) {
        return (
            <div className="page">
                <div className="loading-state">
                    <span>Loading puzzle...</span>
                </div>
            </div>
        );
    }

    if (error || !dailyData) {
        return (
            <div className="page">
                <div className="error-state">
                    <span>{error || 'Failed to load puzzle'}</span>
                    <button className="retry-button" onClick={() => window.location.reload()}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const isFinished = progress.status !== Status.InProgress;
    const isAnimating = gameState.type === 'animating';

    return (
        <div className="page daily-page">
            <div className="page-content">
                <InstrumentHeader
                    dayLabel={dailyData.day_label}
                    attacker={dailyData.puzzle.attacker}
                    currentStep={progress.stepIndex}
                    totalSteps={dailyData.puzzle.steps.length}
                    attemptsUsed={progress.attemptsUsed}
                    status={progress.status}
                />

                <Board
                    fen={fen || dailyData.puzzle.start_fen}
                    orientation={dailyData.puzzle.attacker === 'w' ? 'white' : 'black'}
                    onMove={handleMove}
                    lastMove={lastMove ?? undefined}
                    disabled={isFinished || isAnimating}
                />

                <ActionRow
                    isFinished={isFinished}
                    isLoading={isAnimating}
                    onReveal={handleReveal}
                    onShare={handleShare}
                    shareCopied={shareCopied}
                />

                <footer className="daily-footer">
                    <span>New puzzle daily at local midnight</span>
                </footer>
            </div>

            {toast && (
                <div className={`toast ${toast.isError ? 'toast--error' : ''}`}>
                    {toast.message}
                </div>
            )}

            {showModal && (
                <ResultModal
                    status={progress.status}
                    shareText={dailyData.share.text}
                    onShare={handleShare}
                    onClose={() => setShowModal(false)}
                    onReveal={progress.status === Status.Missed ? handleReveal : undefined}
                    shareCopied={shareCopied}
                />
            )}
        </div>
    );
}
